import { Check, Copy, Laptop, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useApp } from "@/lib/AppContext";
import { ApiError, type IssuedWorker, type WorkerNode } from "@/lib/api";
import { formatDate } from "@/lib/format";

/**
 * CAP-5 on a phone — registering the PC that runs Factorium Recorder, and reading its
 * token once.
 *
 * **The one Settings section the phone app draws**, and the exception earns itself rather
 * than opening a door. Everything else under Settings is configuration set up once from a
 * desk (`phoneSurface.ts`); this is the single step between installing the Recorder and it
 * working, it is a name and a token rather than a screenful of switches, and it is asked
 * for at exactly the moment somebody is standing at the PC they just installed it on with
 * their phone in the other hand.
 *
 * It is the desktop section's short form, over the same three calls — register, list,
 * revoke — so a PC registered here is the same row Settings → Recorder lists, and
 * revoking is revoking. What is left out is the desktop's longer prose: neither is a
 * thing to do, and a phone screen that scrolls past the button is a phone screen nobody
 * reaches the end of.
 */
export function PhoneCaptureToken() {
  const { api } = useApp();

  const [devices, setDevices] = useState<WorkerNode[] | null>(null);
  const [issued, setIssued] = useState<IssuedWorker | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setDevices(await api.listCaptureDevices());
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The capture PCs could not be loaded.");
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function register(event: React.FormEvent) {
    event.preventDefault();
    if (busy || name.trim().length === 0) return;

    setBusy(true);
    setProblem(null);

    try {
      setIssued(await api.registerCaptureDevice(name.trim()));
      setName("");
      await refresh();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The PC could not be registered.");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(device: WorkerNode) {
    setBusy(true);
    setProblem(null);

    try {
      await api.revokeCaptureDevice(device.id);
      await refresh();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The PC could not be revoked.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-5 p-4 [animation:omFadeUp_220ms_cubic-bezier(0.4,0,0.2,1)_both]">
      <p className="text-[13px] leading-[1.5] text-[var(--text-secondary)] [text-wrap:pretty]">
        Factorium Recorder sits in the system tray of your own PC and records the way a voice
        recorder on the desk would. Give that PC a name here and it gets a token to file its
        recordings with.
      </p>

      {problem && (
        <p role="alert" className="text-[13px] leading-[1.5] text-[var(--warning)] [text-wrap:pretty]">
          {problem}
        </p>
      )}

      {issued && <IssuedToken issued={issued} onDismiss={() => setIssued(null)} />}

      <form className="flex flex-col gap-2.5" onSubmit={(event) => void register(event)}>
        <label
          htmlFor="phone-capture-name"
          className="text-[11px] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]"
        >
          Register a PC
        </label>
        <input
          id="phone-capture-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Work laptop"
          maxLength={80}
          // 16px, because anything smaller is what makes iOS zoom the page on focus.
          className="w-full rounded-[var(--radius-md)] border border-[var(--input)] bg-[var(--surface-glass)] px-3 py-3 text-[16px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
        />
        <button
          type="submit"
          disabled={busy || name.trim().length === 0}
          className="h-12 w-full rounded-[var(--radius-md)] bg-[var(--primary)] text-[15px] font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
        >
          {busy ? "Working…" : "Register and show the token"}
        </button>
      </form>

      <div className="flex flex-col gap-2">
        <p className="text-[11px] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          Registered PCs
        </p>

        {devices === null ? (
          <p className="text-[13px] text-[var(--text-muted)]">Loading…</p>
        ) : devices.length === 0 ? (
          <p className="text-[13px] leading-[1.5] text-[var(--text-secondary)] [text-wrap:pretty]">
            No capture PC yet. Recordings arrive by upload until you register one — nothing here is
            required to use Factorium.
          </p>
        ) : (
          devices.map((device) => (
            <div
              key={device.id}
              className="flex items-center gap-3 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3.5 py-3"
            >
              <Laptop size={16} strokeWidth={2} className="shrink-0 text-[var(--text-secondary)]" aria-hidden />

              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[15px] font-bold text-[var(--text-primary)]">
                  {device.name ?? "Unnamed PC"}
                </span>
                <span className="text-[12px] leading-[1.4] text-[var(--text-muted)] [text-wrap:pretty]">
                  {describeContact(device)}
                </span>
              </div>

              {device.status === "active" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void revoke(device)}
                  className="h-9 shrink-0 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[13px] font-semibold text-[var(--text-primary)] disabled:opacity-50"
                >
                  Revoke
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * The token, once — the desktop panel's phone shape, and for the same reason it exists
 * there: the Server keeps only a hash, so dismissing this is the last moment the token is
 * anywhere but on the PC it is for.
 *
 * It wraps rather than scrolls sideways. A token in a horizontally scrolling box on a phone
 * is a token somebody reads two thirds of and types wrong, and copying is not always
 * available — a browser can refuse the clipboard, and then the screen is the only copy.
 */
function IssuedToken({ issued, onDismiss }: { issued: IssuedWorker; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(issued.token);
      setCopied(true);
    } catch {
      // A browser that refuses the clipboard still shows the token on screen, which is
      // the part that matters.
    }
  }

  return (
    <section className="flex flex-col gap-3 rounded-[var(--radius-xl)] border border-[var(--warning)] bg-[var(--surface-glass)] p-3.5">
      <div className="flex items-start gap-2.5">
        <TriangleAlert size={16} strokeWidth={2} className="mt-[3px] shrink-0 text-[var(--warning)]" aria-hidden />
        <div className="min-w-0">
          <h2 className="text-[16px] font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
            Copy this token now
          </h2>
          <p className="mt-1 text-[13px] leading-[1.5] text-[var(--text-secondary)] [text-wrap:pretty]">
            {issued.worker.name} is registered. This is the only time the token is shown — the Server
            keeps a hash of it and cannot show it again. If you lose it, register the PC again.
          </p>
        </div>
      </div>

      <code className="block break-all rounded-[var(--radius-md)] bg-[var(--surface-1)] px-3 py-2.5 font-mono text-[13px] leading-[1.5] text-[var(--text-primary)]">
        {issued.token}
      </code>

      <p className="text-[12px] leading-[1.5] text-[var(--text-secondary)] [text-wrap:pretty]">
        Paste it into Factorium Recorder on that PC, under <strong>Settings → Capture token</strong>,
        with this Server's address beside it.
      </p>

      <button
        type="button"
        onClick={() => void copy()}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-[var(--radius-md)] bg-[var(--primary)] text-[15px] font-semibold text-[var(--primary-foreground)]"
      >
        {copied ? <Check size={16} strokeWidth={2} aria-hidden /> : <Copy size={16} strokeWidth={2} aria-hidden />}
        {copied ? "Copied" : "Copy token"}
      </button>

      <button type="button" onClick={onDismiss} className="h-11 w-full text-[13px] text-[var(--text-muted)]">
        I have copied it
      </button>
    </section>
  );
}

function describeContact(device: WorkerNode): string {
  if (device.status === "revoked")
    return `Revoked ${formatDate(device.revokedAt) || "recently"} — this PC can no longer file recordings.`;

  return device.lastHeartbeatAt === null
    ? "Registered, but it has never connected. Has the token been pasted into Factorium Recorder?"
    : `Last connected ${formatDate(device.lastHeartbeatAt)}.`;
}
